import { useRef } from 'react';
import { useNavigate } from 'react-router';

import type { ContactAddressDTO } from '@invenco/common-interface/shared';
import { AsnStatus, type CreateAsnDTO } from '@invenco/supply-interface';

import { Page } from '@/components/layout';
import {
  WizardActions,
  WizardCancel,
  WizardContent,
  WizardHeader,
  WizardProgress,
  WizardRoot,
  WizardTitle,
} from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { useGateways } from '@/gateways/GatewayProvider';
import { removeFalsyKeys } from '@/shared/helpers/object';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { AddressStep } from './address-step';
import { ExpectedDateStep } from './expected-date-step';
import { ItemsStep } from './items-step';
import { NotesStep } from './notes-step';
import { ReferenceStep } from './reference-step';
import { WarehouseStep } from './warehouse-step';

const steps = [
  { id: 'items', label: 'Items', required: true },
  { id: 'warehouse', label: 'To warehouse', required: true },
  { id: 'address', label: 'From address', required: true },
  { id: 'expectedDate', label: 'Expected date', required: true },
  { id: 'reference', label: 'Reference' },
  { id: 'notes', label: 'Notes' },
] satisfies WizardStepDefinition[];

const getAsnCreateDTO = (asn: Partial<CreateAsnDTO>) =>
  ({
    ...asn,
    status: AsnStatus.DRAFT,
    fromAddress: asn.fromAddress
      ? (removeFalsyKeys(asn.fromAddress) as ContactAddressDTO)
      : undefined,
    asnLines: asn.asnLines ?? [],
  }) as CreateAsnDTO;

export function AsnWizard() {
  const navigate = useNavigate();
  const messages = useMessages();
  const { supplyGateway } = useGateways();
  const draftRef = useRef<Partial<CreateAsnDTO>>({});

  const { mutate: createAsn } = useGatewayMutation({
    mutationFn: () => supplyGateway.createAsn(getAsnCreateDTO(draftRef.current)),
    onSuccess: (asn) => {
      messages.success('ASN created', { name: asn.name });
      void navigate(`/supply/asns/${asn.id}`);
    },
    linkedQuery: ['asns', undefined],
  });

  const updateDraft = (input: Partial<CreateAsnDTO>) => {
    draftRef.current = { ...draftRef.current, ...input };
  };

  return (
    <Page>
      <WizardRoot steps={steps}>
        <WizardHeader>
          <WizardCancel to="/supply/asns" />
          <WizardTitle>New ASN</WizardTitle>
          <WizardProgress />
          <WizardActions
            submitLabel="Create ASN"
            onSubmit={async () => {
              await createAsn(undefined);
            }}
          />
        </WizardHeader>

        <WizardContent>
          <ItemsStep step={steps[0]} onChange={updateDraft} />
          <WarehouseStep step={steps[1]} onChange={updateDraft} />
          <AddressStep step={steps[2]} onChange={updateDraft} />
          <ExpectedDateStep step={steps[3]} onChange={updateDraft} />
          <ReferenceStep step={steps[4]} onChange={updateDraft} />
          <NotesStep step={steps[5]} onChange={updateDraft} />
        </WizardContent>
      </WizardRoot>
    </Page>
  );
}
